<script lang="ts">
  import { t } from '../lib/i18n.svelte'
  import { barsLabel } from '../lib/signal'
  import { onDestroy, onMount } from 'svelte'
  import {
    hideMini,
    onMiniState,
    requestMiniState,
    sendMiniAction,
    type MiniState,
  } from '../lib/tauri'

  /**
   * The mini view: a small always-on-top card with the essentials while you
   * are in a game. It owns no stream state; the host screen publishes
   * snapshots and this only renders them and reports button presses.
   */
  const MAX_SHOWN = 4
  let snap = $state<MiniState | null>(null)
  let seen = $state(0)
  let unlisten: (() => void) | null = null

  const unread = $derived(snap ? Math.max(0, snap.chatCount - seen) : 0)
  const healthLabel = $derived(
    snap?.health === 'slow' ? t('Slow') : snap?.health === 'busy' ? t('Busy') : t('Great'),
  )

  onMount(async () => {
    document.documentElement.classList.add('mini-window')
    unlisten = await onMiniState((next) => (snap = next))
    await requestMiniState()
  })

  onDestroy(() => unlisten?.())

  function openChat() {
    if (snap) seen = snap.chatCount
    void sendMiniAction('chat')
  }
</script>

<div class="mini" data-tauri-drag-region>
  {#if snap}
    <div class="mini__row" data-tauri-drag-region>
      <span class="live-pill mini__live"><span class="dot"></span>LIVE</span>
      <span class="mini__time">{snap.elapsed.replace(/^00:/, '')}</span>
      <span class="mini__spacer"></span>
      <span class="mini__health mini__health--{snap.health}">
        <span class="dot"></span>{healthLabel}
      </span>
    </div>
    <div class="mini__stats" data-tauri-drag-region>
      <div class="mini__stat">
        <strong>{snap.fps ?? '–'}</strong><span>FPS</span>
      </div>
      <div class="mini__stat">
        <strong>{snap.mbps ?? '–'}</strong><span>Mbps</span>
      </div>
      <div class="mini__stat">
        <strong>{snap.encodeMs ?? '–'}</strong><span>{t('ms encode')}</span>
      </div>
    </div>
    <div class="mini__who" data-tauri-drag-region>
      <div class="mini__count">
        {snap.viewers.length === 0
          ? t('Nobody yet')
          : t('{n} on the sofa', { n: snap.viewers.length })}
      </div>
      {#each snap.viewers.slice(0, MAX_SHOWN) as viewer (viewer.id)}
        <div class="mini__viewer" class:mini__viewer--pending={!viewer.connected}>
          <span class="mini__face" class:mini__face--pending={!viewer.connected}>
            {viewer.name.slice(0, 1).toUpperCase()}
          </span>
          <span class="mini__name">{viewer.name}</span>
          {#if viewer.connected}
            {#if viewer.dropped > 0}<span class="mini__drops">{viewer.dropped} {t('dropped')}</span
              >{/if}
            <span class="mini__bars mini__bars--{viewer.bars}" aria-label={barsLabel(viewer.bars)}>
              {#each [1, 2, 3, 4] as bar (bar)}<i class:on={bar <= viewer.bars}></i>{/each}
            </span>
          {:else}
            <span class="mini__drops">{t('Connecting…')}</span>
          {/if}
        </div>
      {/each}
      {#if snap.viewers.length > MAX_SHOWN}
        <div class="mini__more">+{snap.viewers.length - MAX_SHOWN}</div>
      {/if}
    </div>
    <div class="mini__row mini__actions">
      <button type="button" class="mini__button mini__button--wide" onclick={openChat}>
        {t('Chat')}
        {#if unread > 0}<span class="mini__badge">{unread}</span>{/if}
      </button>
      <button
        type="button"
        class="mini__button"
        aria-label={t('Open Sofa')}
        onclick={() => sendMiniAction('expand')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"
          ><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" /></svg
        >
      </button>
      <button
        type="button"
        class="mini__button"
        aria-label={t('Close mini view')}
        onclick={() => hideMini().catch(() => {})}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
      <button
        type="button"
        class="mini__button mini__button--stop"
        aria-label={t('Stop sharing')}
        onclick={() => sendMiniAction('stop')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"
          ><rect x="6" y="6" width="12" height="12" rx="2" /></svg
        >
      </button>
    </div>
  {:else}
    <div class="mini__row"><span class="mini__count">{t('Waiting for the stream…')}</span></div>
  {/if}
</div>
